import { useState } from 'react'
import { Link } from 'react-router-dom'

const DocsPage = () => {
  const [activeSection, setActiveSection] = useState('intro')

  const scrollToSection = (sectionId: string) => {
    setActiveSection(sectionId)
    const element = document.getElementById(sectionId)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const sections = [
    {
      id: 'intro',
      title: '平台介绍',
      content: (
        <div>
          <h2 className="text-2xl font-bold mb-4">AI Gateway Platform</h2>
          <p className="mb-4">
            欢迎使用 AI Gateway Platform！这是一个简单易用的 AI 网关平台，帮助您轻松访问和管理多个 AI 模型。
          </p>
          <p className="mb-4">
            无论您是开发人员、企业用户还是个人用户，我们都提供了直观的界面和强大的功能，让您能够：
          </p>
          <ul className="list-disc list-inside space-y-2 mb-6">
            <li>统一管理多个 AI 模型的访问</li>
            <li>使用 API Key 进行安全认证</li>
            <li>跟踪使用情况和费用</li>
            <li>设置访问限制和安全措施</li>
          </ul>
          <p>
            本指南将帮助您快速上手平台的各项功能，从注册到使用 API Key 调用 AI 模型。
          </p>
        </div>
      )
    },
    {
      id: 'getting-started',
      title: '快速开始',
      content: (
        <div>
          <h2 className="text-2xl font-bold mb-4">快速开始</h2>
          <h3 className="text-xl font-semibold mb-3">第一步：注册账户</h3>
          <ol className="list-decimal list-inside space-y-2 mb-6">
            <li>点击页面右上角的 "注册" 按钮</li>
            <li>填写您的邮箱地址和密码（至少8个字符）</li>
            <li>如果有邀请码，可以填写（可选）</li>
            <li>点击 "注册" 完成账号创建</li>
          </ol>
          
          <h3 className="text-xl font-semibold mb-3">第二步：验证邮箱</h3>
          <ol className="list-decimal list-inside space-y-2 mb-6">
            <li>注册成功后，查收系统发送的验证邮件</li>
            <li>点击邮件中的验证链接完成邮箱验证</li>
            <li>验证成功后，您就可以登录系统了</li>
          </ol>
          
          <h3 className="text-xl font-semibold mb-3">第三步：创建 API Key</h3>
          <ol className="list-decimal list-inside space-y-2 mb-6">
            <li>登录系统后，进入 "API Keys" 页面</li>
            <li>点击 "创建API Key" 按钮</li>
            <li>为您的 API Key 起一个名称</li>
            <li>点击 "创建" 按钮生成 API Key</li>
            <li>复制生成的 API Key 并妥善保存（只显示一次）</li>
          </ol>
          
          <h3 className="text-xl font-semibold mb-3">第四步：开始使用</h3>
          <p className="mb-4">
            使用您的 API Key 调用 AI 模型，只需在请求头中添加：
          </p>
          <div className="bg-gray-100 p-4 rounded-md mb-6 font-mono text-sm">
            Authorization: Bearer YOUR_API_KEY
          </div>
        </div>
      )
    },
    {
      id: 'api-key-guide',
      title: 'API Key 使用指南',
      content: (
        <div>
          <h2 className="text-2xl font-bold mb-4">API Key 使用指南</h2>
          
          <h3 className="text-xl font-semibold mb-3">什么是 API Key？</h3>
          <p className="mb-4">
            API Key 是您访问 AI Gateway Platform 的唯一凭证，类似于一把钥匙，让您能够安全地调用平台上的 AI 模型。
          </p>
          
          <h3 className="text-xl font-semibold mb-3">如何创建 API Key</h3>
          <ol className="list-decimal list-inside space-y-2 mb-6">
            <li>登录系统后，点击左侧菜单中的 "API Keys"</li>
            <li>点击 "创建新 API Key" 按钮</li>
            <li>输入一个描述性的名称，方便您识别这个 Key 的用途</li>
            <li>（可选）设置速率限制，防止过度使用</li>
            <li>（可选）设置 IP 白名单，增加安全性</li>
            <li>点击 "创建" 按钮</li>
            <li>复制并保存生成的 API Key（只显示一次）</li>
          </ol>
          
          <h3 className="text-xl font-semibold mb-3">如何使用 API Key</h3>
          <p className="mb-4">
            在您的 API 请求中，需要在请求头中包含您的 API Key：
          </p>
          <div className="bg-gray-100 p-4 rounded-md mb-6 font-mono text-sm">
            curl -H "Authorization: Bearer YOUR_API_KEY" https://api.example.com/chat/completions
          </div>
          
          <h3 className="text-xl font-semibold mb-3">API Key 安全提示</h3>
          <ul className="list-disc list-inside space-y-2 mb-6">
            <li>不要在代码中硬编码 API Key</li>
            <li>不要在公开场合分享您的 API Key</li>
            <li>定期更新 API Key 以提高安全性</li>
            <li>如果怀疑 API Key 泄露，立即撤销并创建新的</li>
          </ul>
        </div>
      )
    },
    {
      id: 'usage-guide',
      title: '使用指南',
      content: (
        <div>
          <h2 className="text-2xl font-bold mb-4">使用指南</h2>
          
          <h3 className="text-xl font-semibold mb-3">调用 AI 模型</h3>
          <p className="mb-4">
            您可以使用 API Key 调用各种 AI 模型，包括聊天、文本生成、图像处理等功能。
          </p>
          <div className="bg-gray-100 p-4 rounded-md mb-6 font-mono text-sm">
            <p className="font-semibold mb-2">示例请求格式：</p>
            <p>POST /v1/chat/completions</p>
            <p>Content-Type: application/json</p>
            <p>Authorization: Bearer YOUR_API_KEY</p>
            <p></p>
            <p>{'{"model": "gpt-3.5-turbo", "messages": [{"role": "user", "content": "你好"}]}'}</p>
          </div>
          
          <h3 className="text-xl font-semibold mb-3">查看使用统计</h3>
          <ol className="list-decimal list-inside space-y-2 mb-6">
            <li>登录系统后，进入 "使用统计" 页面</li>
            <li>查看 API 调用次数、使用的 token 数量和费用</li>
            <li>可以按时间范围、模型类型和 API Key 筛选数据</li>
          </ol>
          
          <h3 className="text-xl font-semibold mb-3">管理设备（可选）</h3>
          <p className="mb-4">
            如果您需要在多个设备上使用平台，可以：
          </p>
          <ol className="list-decimal list-inside space-y-2 mb-6">
            <li>进入 "设备管理" 页面</li>
            <li>添加新设备并为其设置名称</li>
            <li>可以为设备设置使用配额，控制使用量</li>
          </ol>
        </div>
      )
    },
    {
      id: 'billing',
      title: '计费说明',
      content: (
        <div>
          <h2 className="text-2xl font-bold mb-4">计费说明</h2>
          
          <h3 className="text-xl font-semibold mb-3">费用计算</h3>
          <p className="mb-4">
            平台采用透明的计费方式，根据您使用的 AI 模型和 token 数量收费。
          </p>
          <ul className="list-disc list-inside space-y-2 mb-6">
            <li>不同 AI 模型有不同的计费标准</li>
            <li>费用基于输入（prompt）和输出（completion）的 token 数量</li>
            <li>每次 API 调用后，系统会自动计算并扣除相应费用</li>
          </ul>
          
          <h3 className="text-xl font-semibold mb-3">余额管理</h3>
          <ol className="list-decimal list-inside space-y-2 mb-6">
            <li>登录系统后，在 "账单" 页面查看当前余额</li>
            <li>联系管理员进行充值操作</li>
            <li>查看详细的交易记录，了解费用明细</li>
          </ol>
          
          <h3 className="text-xl font-semibold mb-3">费用优化建议</h3>
          <ul className="list-disc list-inside space-y-2 mb-6">
            <li>优化提示词，减少不必要的 token 使用</li>
            <li>合理设置 API Key 的速率限制，避免意外的大量调用</li>
            <li>定期查看使用统计，了解费用情况</li>
          </ul>
        </div>
      )
    },
    {
      id: 'faq',
      title: '常见问题',
      content: (
        <div>
          <h2 className="text-2xl font-bold mb-4">常见问题</h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold mb-2">注册与登录</h3>
              <ul className="list-disc list-inside space-y-2">
                <li><strong>邮箱已被注册</strong>：请使用其他邮箱注册，或尝试找回密码</li>
                <li><strong>邀请码无效</strong>：请检查邀请码是否正确，或联系邀请人确认</li>
                <li><strong>邮箱未验证</strong>：请检查邮箱并点击验证链接，如未收到邮件可在个人设置中重发</li>
                <li><strong>密码错误</strong>：请尝试重置密码，或联系技术支持</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold mb-2">API Key 相关</h3>
              <ul className="list-disc list-inside space-y-2">
                <li><strong>API Key 无效</strong>：请检查 API Key 是否正确，或是否已被撤销</li>
                <li><strong>API Key 过期</strong>：请创建新的 API Key 并更新您的应用</li>
                <li><strong>IP 白名单限制</strong>：请检查您的 IP 是否在白名单中，或联系管理员调整</li>
                <li><strong>API Key 泄露</strong>：立即在系统中撤销该 Key 并创建新的</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold mb-2">使用问题</h3>
              <ul className="list-disc list-inside space-y-2">
                <li><strong>余额不足</strong>：请联系管理员进行充值</li>
                <li><strong>调用失败</strong>：检查网络连接和 API Key 是否正确</li>
                <li><strong>响应缓慢</strong>：可能是模型处理时间较长，或网络延迟</li>
                <li><strong>费用异常</strong>：请联系管理员核查费用计算</li>
              </ul>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'support',
      title: '技术支持',
      content: (
        <div>
          <h2 className="text-2xl font-bold mb-4">技术支持</h2>
          <p className="mb-4">
            如果您遇到任何问题，可以通过以下方式获取帮助：
          </p>
          <ul className="list-disc list-inside space-y-2 mb-6">
            <li><strong>邮箱支持</strong>：support@ai-gateway-platform.com</li>
            <li><strong>在线客服</strong>：登录系统后，点击右下角的 "在线客服" 按钮</li>
            <li><strong>帮助文档</strong>：您正在阅读的就是我们的帮助文档</li>
          </ul>
          <p className="mb-4">
            我们的支持团队会在工作时间内尽快回复您的问题。
          </p>
        </div>
      )
    },
    {
      id: 'best-practices',
      title: '最佳实践',
      content: (
        <div>
          <h2 className="text-2xl font-bold mb-4">最佳实践</h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold mb-3">API Key 管理</h3>
              <ul className="list-disc list-inside space-y-2">
                <li><strong>妥善保管</strong>：API Key 是访问系统的凭证，不要分享给他人</li>
                <li><strong>定期轮换</strong>：建议每 3-6 个月重新生成一次 API Key</li>
                <li><strong>设置限制</strong>：根据实际需求设置速率限制，避免滥用</li>
                <li><strong>使用环境变量</strong>：在代码中使用环境变量存储 API Key，不要硬编码</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold mb-3">提示词优化</h3>
              <ul className="list-disc list-inside space-y-2">
                <li><strong>清晰具体</strong>：提供明确的指令和上下文</li>
                <li><strong>控制长度</strong>：避免过于冗长的提示词，减少 token 使用</li>
                <li><strong>结构化输入</strong>：使用格式化的输入，提高模型理解度</li>
                <li><strong>迭代改进</strong>：根据模型响应不断调整提示词</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold mb-3">费用管理</h3>
              <ul className="list-disc list-inside space-y-2">
                <li><strong>监控使用</strong>：定期查看使用统计，了解费用情况</li>
                <li><strong>设置预算</strong>：根据业务需求，设置合理的使用预算</li>
                <li><strong>使用缓存</strong>：对于重复的查询，考虑使用缓存减少 API 调用</li>
                <li><strong>选择合适模型</strong>：根据任务复杂度选择适合的模型，平衡效果和成本</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold mb-3">系统使用</h3>
              <ul className="list-disc list-inside space-y-2">
                <li><strong>及时验证邮箱</strong>：注册后及时验证邮箱，避免影响系统使用</li>
                <li><strong>合理使用邀请码</strong>：分享邀请码给需要使用系统的同事或朋友</li>
                <li><strong>遵守使用条款</strong>：合理使用系统资源，避免滥用</li>
                <li><strong>保持更新</strong>：关注系统更新和新功能</li>
              </ul>
            </div>
          </div>
        </div>
      )
    }
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="text-xl font-bold text-primary-600">
                AI Gateway
              </Link>
            </div>
            <div className="flex items-center space-x-4">
              <Link to="/login" className="text-sm text-gray-600 hover:text-gray-900">
                登录
              </Link>
              <Link to="/register" className="text-sm text-primary-600 hover:text-primary-500">
                注册
              </Link>
            </div>
          </div>
        </div>
      </header>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          <h1 className="text-3xl font-bold text-gray-900">用户指南</h1>
          
          <div className="flex space-x-6">
            <div className="w-64 flex-shrink-0">
              <div className="sticky top-6">
                <nav className="space-y-1">
                  {sections.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className={`w-full text-left px-3 py-2 text-sm font-medium rounded-md transition-colors ${activeSection === section.id ? 'bg-primary-50 text-primary-700' : 'text-gray-700 hover:bg-gray-50'}`}
                      aria-label={`导航到${section.title}部分`}
                    >
                      {section.title}
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            <div className="flex-1 bg-white rounded-lg shadow-sm p-6">
              <div className="prose max-w-none">
                {sections.map((section) => (
                  <div key={section.id} id={section.id} className="mb-12 scroll-mt-24">
                    {section.content}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <footer className="bg-white border-t border-gray-200 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center text-sm text-gray-500">
            © 2026 AI Gateway Platform. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  )
}

export default DocsPage
